/**
 * Manual resume builder — select capabilities to include in a resume.
 *
 * Layout:
 *  - Header: job title + company
 *  - Left sidebar: posting keywords and requirements (reference only)
 *  - Right panel: capability picker organized by tenure → title → experience
 *  - Each bullet has a checkbox + keyword-highlighted text
 *  - "Build Resume" button creates the resume and navigates to /resumes/:id
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, AlertCircle, Loader2, Pencil, Sparkles, Check, X } from 'lucide-react';
import {
  jobPostingApi,
  capabilityApi,
  tenureApi,
  titleApi,
  experienceApi,
  resumeApi,
  authApi,
  educationApi,
} from '@/lib/api';
import type { JobPosting, Capability, Tenure, Title, Experience } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Navigation } from '@/components/Navigation';
import { SkillBucketsSection } from '@/components/SkillBucketsSection';
import { BulletRewritePanel } from '@/components/BulletRewritePanel';
import { createLocalSkillRepository, type SkillGroup } from '@/lib/skillRepository';
import { showErrorToast } from '@/lib/toast';

interface HierarchyNode {
  tenure: Tenure;
  titles: {
    title: Title;
    experiences: {
      experience: Experience;
      capabilities: Capability[];
    }[];
  }[];
}

function highlightKeywords(text: string, keywords: string[]): React.ReactNode {
  if (keywords.length === 0) return text;

  const pattern = new RegExp(
    `(${keywords
      .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|')})`,
    'gi'
  );

  const parts = text.split(pattern);
  return parts.map((part, i) =>
    keywords.some((k) => k.toLowerCase() === part.toLowerCase()) ? (
      <mark key={i} className="bg-yellow-100 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

interface CapabilityBulletProps {
  cap: Capability;
  keywords: string[];
  isSelected: boolean;
  onToggle: (id: string, checked: boolean) => void;
  onBodyChange: (id: string, body: string) => void;
}

function CapabilityBullet({ cap, keywords, isSelected, onToggle, onBodyChange }: CapabilityBulletProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(cap.body);
  const [showRewrite, setShowRewrite] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setEditText(cap.body); }, [cap.body]);
  useEffect(() => { if (editing) textareaRef.current?.focus(); }, [editing]);

  const updateMutation = useMutation({
    mutationFn: (body: string) => capabilityApi.update(cap.id, { body }),
    onSuccess: (updated) => {
      onBodyChange(cap.id, updated.body);
      void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
    },
  });

  const handleSave = () => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== cap.body) updateMutation.mutate(trimmed);
    setEditing(false);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) handleSave();
    if (e.key === 'Escape') { setEditText(cap.body); setEditing(false); }
  };

  return (
    <div className="flex items-start gap-3 group p-2 rounded hover:bg-accent">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => onToggle(cap.id, e.target.checked)}
        className="mt-1 shrink-0 cursor-pointer"
      />
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex flex-col gap-1">
            <textarea
              ref={textareaRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKey}
              rows={3}
              className="w-full rounded border border-input bg-background px-2 py-1 text-sm leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="flex gap-1.5">
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Check className="h-3 w-3" /> {updateMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => { setEditText(cap.body); setEditing(false); }}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs border hover:bg-muted"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-1">
            <span className="flex-1 text-sm leading-relaxed cursor-pointer" onClick={() => onToggle(cap.id, !isSelected)}>
              {highlightKeywords(cap.body, keywords)}
            </span>
            <div className="flex shrink-0 gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => setEditing(true)}
                title="Edit capability"
                className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setShowRewrite((v) => !v)}
                title="Rewrite with AI"
                className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Sparkles className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
        {showRewrite && !editing && (
          <BulletRewritePanel
            capabilityId={cap.id}
            currentBody={cap.body}
            onAccept={(_format, suggestion) => {
              updateMutation.mutate(suggestion);
              setShowRewrite(false);
            }}
            onAdoptSkills={async (skills) => {
              await capabilityApi.update(cap.id, { skills: [...(cap.skills ?? []), ...skills] });
              void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
            }}
            onDismiss={() => setShowRewrite(false)}
          />
        )}
      </div>
    </div>
  );
}

export default function ResumeBuilderPage() {
  const { postingId } = useParams<{ postingId: string }>();
  const navigate = useNavigate();
  const [selectedCapabilityIds, setSelectedCapabilityIds] = useState<Set<string>>(
    new Set()
  );
  const [selectedEducationIds, setSelectedEducationIds] = useState<Set<string>>(
    new Set()
  );
  const localRepo = useMemo(() => createLocalSkillRepository(), []);
  const [skillGroups, setSkillGroups] = useState<SkillGroup[]>([]);

  // Auth guard
  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
  });

  if (!authLoading && !authData) {
    void navigate('/login');
    return null;
  }

  // Load posting
  const { data: posting, isLoading: postingLoading } = useQuery({
    queryKey: ['postings', postingId],
    queryFn: () => jobPostingApi.getById(postingId!),
    enabled: !!postingId,
  });

  // Load all data separately
  const { data: tenures = [] } = useQuery({
    queryKey: ['tenures'],
    queryFn: tenureApi.list,
  });

  const { data: titles = [] } = useQuery({
    queryKey: ['titles'],
    queryFn: () => titleApi.list(),
  });

  const { data: experiences = [] } = useQuery({
    queryKey: ['experiences'],
    queryFn: experienceApi.list,
  });

  const { data: capabilitiesData } = useQuery({
    queryKey: ['capabilities', 'list'],
    queryFn: () => capabilityApi.list({ limit: 200 }),
  });

  const { data: educationEntries = [] } = useQuery({
    queryKey: ['education'],
    queryFn: educationApi.list,
  });

  // Initialize selected education IDs with all entries by default
  useEffect(() => {
    if (educationEntries.length > 0) {
      setSelectedEducationIds(new Set(educationEntries.map((e) => e.id)));
    }
  }, [educationEntries]);

  // Build hierarchy
  const hierarchy = useMemo(() => {
    const allCaps = capabilitiesData?.items ?? [];
    const capsByExp = new Map<string, Capability[]>();

    allCaps.forEach((cap) => {
      if (!capsByExp.has(cap.experienceId)) {
        capsByExp.set(cap.experienceId, []);
      }
      capsByExp.get(cap.experienceId)!.push(cap);
    });

    const titlesByTenure = new Map<string, Title[]>();
    titles.forEach((t) => {
      if (!titlesByTenure.has(t.tenureId)) {
        titlesByTenure.set(t.tenureId, []);
      }
      titlesByTenure.get(t.tenureId)!.push(t);
    });

    const expsByTitle = new Map<string, Experience[]>();
    experiences.forEach((e) => {
      if (!expsByTitle.has(e.titleId)) {
        expsByTitle.set(e.titleId, []);
      }
      expsByTitle.get(e.titleId)!.push(e);
    });

    const result: HierarchyNode[] = tenures
      .sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? ''))
      .map((tenure) => {
        const tenureTitles = titlesByTenure.get(tenure.id) ?? [];
        const titleNodes = tenureTitles
          .sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? ''))
          .map((title) => {
            const titleExps = expsByTitle.get(title.id) ?? [];
            const expNodes = titleExps.map((exp) => ({
              experience: exp,
              capabilities: (capsByExp.get(exp.id) ?? []).sort(
                (a, b) => a.sortOrder - b.sortOrder
              ),
            }));
            return { title, experiences: expNodes };
          });

        return { tenure, titles: titleNodes };
      });

    return result;
  }, [tenures, titles, experiences, capabilitiesData]);

  const buildMutation = useMutation({
    mutationFn: () =>
      resumeApi.createManual(
        postingId!,
        Array.from(selectedCapabilityIds),
        Array.from(selectedEducationIds),
        skillGroups
      ),
    onSuccess: (resume) => {
      navigate(`/resumes/${resume.id}`);
    },
    onError: (error: any) => {
      showErrorToast(error.message || 'Failed to build resume');
    },
  });

  const isLoading =
    authLoading || postingLoading || !capabilitiesData;
  const selectedCount = selectedCapabilityIds.size;
  const totalBullets = (capabilitiesData?.items ?? []).length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="mx-auto max-w-7xl px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    );
  }

  if (!posting) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="mx-auto max-w-7xl px-4 py-8">
          <Card>
            <CardContent className="pt-8">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <p>Posting not found</p>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const displayTitle = posting.title ?? posting.requirements?.role ?? '(Untitled)';
  const displayCompany = posting.hiringCompany ?? '—';
  const keywords = posting.requirements?.keywords ?? [];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="mx-auto max-w-7xl px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/postings')}
            className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Postings
          </button>
          <div>
            <h1 className="text-3xl font-bold">{displayTitle}</h1>
            <p className="text-muted-foreground">{displayCompany}</p>
          </div>
        </div>

        {/* Main layout: sidebar + content */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* Left sidebar: posting keywords */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardHeader>
                <h2 className="text-sm font-semibold">Keywords</h2>
              </CardHeader>
              <CardContent>
                {keywords.length > 0 ? (
                  <div className="space-y-2">
                    {keywords.map((kw) => (
                      <div
                        key={kw}
                        className="rounded-md bg-yellow-50 px-2.5 py-1.5 text-xs font-medium text-yellow-900 border border-yellow-200"
                      >
                        {kw}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No keywords extracted
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right content: capability picker */}
          <div className="lg:col-span-3 space-y-4">
            {/* Selection summary */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-600" />
                    <p className="text-sm font-medium">
                      {selectedCount} bullet{selectedCount !== 1 ? 's' : ''} selected
                      {selectedCount > 0 && ` · ${selectedCount} / ${totalBullets} available`}
                    </p>
                  </div>
                  <Button
                    onClick={() => buildMutation.mutate()}
                    disabled={
                      selectedCount === 0 ||
                      buildMutation.isPending
                    }
                    size="sm"
                  >
                    {buildMutation.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                        Building…
                      </>
                    ) : (
                      'Build Resume'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Capability hierarchy */}
            {hierarchy.length === 0 ? (
              <Card>
                <CardContent className="pt-8">
                  <div className="flex items-center gap-2 text-muted-foreground text-center justify-center">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <p>No capabilities found. Create some first in the Capabilities tab.</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              hierarchy.map((tenureNode) => (
                <Card key={tenureNode.tenure.id}>
                  <CardHeader className="pb-3">
                    <h2 className="font-semibold text-base">
                      {tenureNode.tenure.company}
                    </h2>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-4">
                    {tenureNode.titles.map((titleNode) => (
                      <div key={titleNode.title.id} className="space-y-2">
                        <h3 className="font-medium text-sm text-primary">
                          {titleNode.title.title}
                        </h3>

                        {titleNode.experiences.map((expNode) => (
                          <div key={expNode.experience.id} className="ml-4 space-y-1.5">
                            <p className="text-xs text-muted-foreground italic">
                              {[
                                expNode.experience.project,
                                expNode.experience.team,
                                expNode.experience.role,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>

                            {expNode.capabilities.map((cap) => (
                              <CapabilityBullet
                                key={cap.id}
                                cap={cap}
                                keywords={keywords}
                                isSelected={selectedCapabilityIds.has(cap.id)}
                                onToggle={(id, checked) => {
                                  const newSelected = new Set(selectedCapabilityIds);
                                  if (checked) newSelected.add(id);
                                  else newSelected.delete(id);
                                  setSelectedCapabilityIds(newSelected);
                                }}
                                onBodyChange={(_id, _body) => {}}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))
            )}

            {/* Education Section */}
            {educationEntries.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold text-base">Education</h2>
                </CardHeader>
                <CardContent className="space-y-3">
                  {educationEntries.map((edu) => (
                    <label
                      key={edu.id}
                      className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEducationIds.has(edu.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedEducationIds);
                          if (e.target.checked) {
                            newSelected.add(edu.id);
                          } else {
                            newSelected.delete(edu.id);
                          }
                          setSelectedEducationIds(newSelected);
                        }}
                        className="mt-1 shrink-0 cursor-pointer"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-sm">{edu.institution}</h3>
                        <p className="text-sm text-muted-foreground">{edu.degreeAndMajor}</p>
                        {edu.graduationDate && (
                          <p className="text-xs text-muted-foreground">
                            Graduated {new Date(edu.graduationDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                            })}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Skill Buckets Section */}
            <div className="mt-6">
              <h2 className="text-lg font-semibold mb-4">Skill Organization</h2>
              <SkillBucketsSection
                repository={localRepo}
                unclassifiedLabel="Don't Include"
                unclassifiedVariant="exclusion"
                onSkillGroupsChange={setSkillGroups}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
